/**
 * Deliveries (stops) API — driver-facing.
 */
import { apiClient } from './api';

export type StopStatus = 'pending' | 'in_transit' | 'delivered' | 'failed' | 'skipped';

export interface DeliveryStop {
  id: number;
  delivery_route_id: number;
  order_id: number;
  order_total: number;
  customer_id: number | null;
  customer_name: string | null;
  customer_address: string | null;
  customer_phone: string | null;
  sequence: number;
  status: StopStatus;
  scheduled_window_start: string | null;
  scheduled_window_end: string | null;
  attempted_at: string | null;
  completed_at: string | null;
  failure_reason: string | null;
  failure_notes: string | null;
  pod_signature_url: string | null;
  pod_photo_url: string | null;
  pod_received_by: string | null;
  pod_latitude: number | null;
  pod_longitude: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeliverStopInput {
  pod_signature_url?: string;
  pod_photo_url?: string;
  /** Recipient name (VP-DRV-004) — required by the backend. */
  pod_received_by?: string;
  /** GPS captured at the moment of proof-of-delivery (VP-DRV-004). */
  pod_latitude?: number;
  pod_longitude?: number;
  notes?: string;
}

export interface FailStopInput {
  failure_reason: string;
  failure_notes?: string;
  /** GPS captured at the moment of the failed-delivery attempt (VP-DRV-004). */
  pod_latitude?: number;
  pod_longitude?: number;
}

export interface DriverDashboard {
  active_route_id: number | null;
  total_stops: number;
  completed_stops: number;
  failed_stops: number;
  remaining_stops: number;
  total_collected_today: number;
  collections_count_today: number;
  failed_today: number;
}

export interface DriverPerformance {
  delivery_rate: number;
  on_time_rate: number;
  failed_rate: number;
  total_collected: number;
  total_stops: number;
  delivered_stops: number;
  failed_stops: number;
  daily: Array<{ date: string; delivered: number; failed: number; collected: number }>;
}

function unwrapList<T>(raw: any): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (Array.isArray(raw?.data)) return raw.data as T[];
  return [];
}

export async function listStopsForRoute(routeId: number | string): Promise<DeliveryStop[]> {
  const raw = await apiClient.get<any>(`/delivery-routes/${routeId}/stops`);
  return unwrapList<DeliveryStop>(raw);
}

export async function listMyDriverStops(filters: { status?: StopStatus; from?: string; to?: string } = {}): Promise<DeliveryStop[]> {
  const raw = await apiClient.get<any>('/drivers/me/stops', filters as Record<string, any>);
  return unwrapList<DeliveryStop>(raw);
}

export async function listMyCustomerDeliveries(filters: { status?: StopStatus; limit?: number } = {}): Promise<DeliveryStop[]> {
  const raw = await apiClient.get<any>('/customers/me/deliveries', filters as Record<string, any>);
  return unwrapList<DeliveryStop>(raw);
}

export async function getDriverDashboard(): Promise<DriverDashboard> {
  return apiClient.get<DriverDashboard>('/drivers/me/dashboard');
}

export async function getDriverPerformance(days: number = 7): Promise<DriverPerformance> {
  return apiClient.get<DriverPerformance>('/drivers/me/performance', { days });
}

export async function getStop(id: number | string): Promise<DeliveryStop> {
  return apiClient.get<DeliveryStop>(`/delivery-stops/${id}`);
}

export async function deliverStop(id: number | string, input: DeliverStopInput): Promise<DeliveryStop> {
  return apiClient.post<DeliveryStop>(`/delivery-stops/${id}/deliver`, input);
}

export async function failStopApi(id: number | string, input: FailStopInput): Promise<DeliveryStop> {
  return apiClient.post<DeliveryStop>(`/delivery-stops/${id}/fail`, input);
}

export async function updateStopStatus(id: number | string, status: StopStatus): Promise<DeliveryStop> {
  return apiClient.patch<DeliveryStop>(`/delivery-stops/${id}/status`, { status });
}
